import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { TrendingUp, TrendingDown, BarChart3, DollarSign, ShoppingCart, FileSignature, ArrowUp, ArrowDown } from 'lucide-react';
import { API, AuthContext } from '../App';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, sub, color = 'amber', testid }) => (
  <Card data-testid={testid} className="p-4">
    <div className={`inline-flex p-2 rounded-lg bg-${color}-100 text-${color}-700 mb-2`}><Icon className="w-5 h-5" /></div>
    <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className="text-2xl font-bold text-foreground">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </Card>
);

export default function SalesPurchaseDashboard() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboards/sales-purchase`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => {
        if (e?.response?.status === 402 || e?.response?.status === 403) toast.error('This dashboard requires the Enterprise plan.');
        else toast.error('Failed to load dashboard');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Layout><div className="py-20 text-center text-muted-foreground">Loading…</div></Layout>;
  if (!data) return <Layout><div className="py-20 text-center text-muted-foreground">No data</div></Layout>;

  const h = data.headline || {};
  const marginColor = h.gross_margin_pct >= 30 ? 'emerald' : h.gross_margin_pct >= 10 ? 'amber' : 'rose';

  return (
    <Layout>
      <div data-testid="sales-purchase-dashboard" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-fuchsia-600" />Sales & Purchase Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">Job-level profit and loss across every approved client LPO and the linked supplier purchases.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard testid="stat-revenue" icon={TrendingUp} label="Revenue (invoiced)" value={formatCurrency(h.revenue, symbol)} sub={`Received ${formatCurrency(h.revenue_received, symbol)}`} color="emerald" />
          <StatCard testid="stat-cost" icon={TrendingDown} label="Cost (POs)" value={formatCurrency(h.cost, symbol)} sub={`Paid ${formatCurrency(h.cost_paid, symbol)}`} color="rose" />
          <StatCard testid="stat-margin" icon={DollarSign} label="Gross Margin" value={formatCurrency(h.gross_margin, symbol)} sub={`${h.gross_margin_pct}% margin`} color={marginColor} />
          <StatCard testid="stat-pipeline" icon={FileSignature} label="Pipeline" value={formatCurrency(h.revenue_pipeline, symbol)} sub={`Cost commitments ${formatCurrency(h.cost_pipeline, symbol)}`} color="indigo" />
        </div>

        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground flex items-center gap-2"><ShoppingCart className="w-4 h-4" />Jobs · per-LPO PnL</h3>
              <p className="text-xs text-muted-foreground">{(data.jobs || []).length} approved client jobs · costs aggregated from purchase orders linked to this job</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Job / LPO</th>
                  <th className="px-4 py-2 text-left">Client</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-right">Cost</th>
                  <th className="px-4 py-2 text-right">Margin</th>
                  <th className="px-4 py-2 text-right">Margin %</th>
                  <th className="px-4 py-2 text-center">POs</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.jobs || []).map(j => {
                  const negative = j.margin < 0;
                  return (
                    <tr key={j.id} data-testid={`job-row-${j.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2"><span className="font-mono text-xs text-muted-foreground">{j.quotation_no}</span><div className="font-medium">{j.title}</div></td>
                      <td className="px-4 py-2">{j.client}</td>
                      <td className="px-4 py-2 text-right font-semibold">{formatCurrency(j.revenue, symbol)}</td>
                      <td className="px-4 py-2 text-right text-rose-600">{formatCurrency(j.cost, symbol)}</td>
                      <td className={`px-4 py-2 text-right font-bold ${negative ? 'text-rose-700' : 'text-emerald-700'}`}>{negative ? <ArrowDown className="w-3 h-3 inline" /> : <ArrowUp className="w-3 h-3 inline" />} {formatCurrency(Math.abs(j.margin), symbol)}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${negative ? 'text-rose-700' : j.margin_pct >= 30 ? 'text-emerald-700' : 'text-amber-700'}`}>{j.margin_pct}%</td>
                      <td className="px-4 py-2 text-center">{j.purchase_count}</td>
                      <td className="px-4 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase">{j.status}</span></td>
                    </tr>
                  );
                })}
                {(!data.jobs || !data.jobs.length) && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">No approved client jobs yet. Create a Sales Quotation, mark approved, and link supplier POs to see job-level PnL here.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-4 bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <BarChart3 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <div className="font-semibold mb-1">How to link a purchase to a sales job</div>
              <p>When creating a Purchase Order or Purchase Quotation, set <code className="px-1 bg-white rounded">parent_sales_id</code> to the Sales Quotation's ID, or fill <code className="px-1 bg-white rounded">client_lpo_ref</code> with the Sales quote number. Costs from those POs will roll up here automatically.</p>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
